import * as types from "./../action-types";
import axios from "axios";
import { get, zipObject } from "lodash";

const cleanUpStore = (commit) => {
  commit("db/account/resetState", null, { root: true });
  commit("db/devices/resetState", null, { root: true });
  commit("db/requests/resetState", null, { root: true });
  commit("db/account/location/resetState", null, { root: true });
};

const account = (commit, action, data) =>
  commit(`db/account/${action}`, data, { root: true });

/**
 * Helper function to find the matching device from PLC eligibility response
 * PLC may return multiple devices due to MAC address matching (wifi_macs, bluetooth_macs, etc.)
 * This function ensures we get the device that matches the requested deviceId
 *
 * @param {Array} devices - Array of device eligibility objects from PLC
 * @param {String} requestedDeviceId - The device ID we're looking for
 * @returns {Object|null} - The matched device or null if not found
 */
function findMatchingDevice(devices, requestedDeviceId) {
  if (!devices || !Array.isArray(devices) || devices.length === 0) {
    return null;
  }

  // Find device where device_id or mac_id matches the requested deviceId
  const matchedDevice = devices.find(
    (device) =>
      device.device_id === requestedDeviceId ||
      device.mac_id === requestedDeviceId
  );

  return matchedDevice || null;
}

async function fetchDeviceDetailed(
  { getters, commit, rootGetters },
  { id, includes = [], customer_auth = true }
) {
  id = String(id).toLowerCase();
  let args = {};
  if (includes && includes.length) {
    args["params"] = { includes };
  }
  if (!customer_auth) {
    args["headers"] = { "X-CUSTOMER-AUTH": 0 };
  }
  const isV3 = rootGetters["auth/isNewLeftNav"] === true;

  let uri;
  if (isV3) {
    uri = `v3/devices/${id}?includes[]=setups_history`;
  } else {
    uri = `v2/devices/${id}/device_lookup`;
  }

  let response = await axios.get(uri, args);

  try {
    let { meta, device, profile } = response.data;

    if (meta.full_data) {
      if (String(getters.doorbotId) !== String(id)) {
        commit("resetState");
      }

      if (!meta["is_alarm_device"]) {
        commit(types.FETCH_DEVICE.SUCCESS, { device, profile });
      }
    } else if (meta.profile_only) {
      cleanUpStore(commit);
      commit(types.FETCH_DEVICE.SUCCESS, { profile });
    }
    return response.data;
  } catch (error) {
    return error;
  }
}

async function getDeviceStatus({ state, commit, getters }) {
  commit(types.RESET_DEVICE_STATUS);
  const device_id = getters.doorbotId,
    type = getters.type,
    sr = getters.lastStatusReport,
    enable_vod = get(sr, "data.current_settings.enable_vod", ""),
    status_report_ts = get(sr, "timestamp", "");
  try {
    const response = await axios.get(`v2/devices/${device_id}/status`, {
      params: { type, enable_vod, status_report_ts },
    });
    commit(types.SET_DEVICE_STATUS.SUCCESS, response.data);
    return true;
  } catch (error) {
    commit(types.SET_DEVICE_STATUS.ERROR);
    throw error;
  }
}

async function getWarrantyAndEligibilityStatus(
  { dispatch },
  {
    device_id,
    customerId: customer_id,
    category,
    doorbot_id,
    locationId: locationId,
  }
) {
  if (category && !["device", "accessory"].includes(category)) {
    throw new Error('Category must be either "device" or "accessory"');
  }
  const devices = [{ device_id, doorbot_id }];

  try {
    const response = await axios.get(`v2/devices/validate_eligibility`, {
      params: { devices, customer_id, category },
    });

    // Get Devices Delayed fulfillment
    const newEligibilityData = await dispatch("getDeviceEligibility", {
      deviceId: device_id,
      customerId: customer_id,
      locationId: locationId,
    });

    const deviceEligibility = get(response, ["data", device_id], {});

    // Add the new properties to the eligibility object
    deviceEligibility.eligibility = {
      ...deviceEligibility.eligibility,
      is_delayed_fulfillment:
        newEligibilityData.eligibility.is_delayed_fulfillment,
      delayed_fulfillment_reason:
        newEligibilityData.eligibility.delayed_fulfillment_reason,
    };

    return deviceEligibility;
  } catch (error) {
    throw error;
  }
}

async function getDeviceEligibility(
  {},
  { deviceId, customerId, locationId, deviceLocationCountry }
) {
  try {
    const eligibilityStatus = await axios.get(`v2/devices/eligibility`, {
      params: {
        ids: [deviceId],
        customer_id: customerId ? customerId : undefined,
        location_id: locationId ? locationId : undefined,
        devices_location_country: deviceLocationCountry
          ? deviceLocationCountry
          : undefined,
      },
    });

    // Use helper function to find the correct device from PLC response
    const matchedDevice = findMatchingDevice(eligibilityStatus.data, deviceId);

    if (!matchedDevice) {
      console.error(
        `Device ${deviceId} not found in eligibility response. Falling back to first device.`
      );
      return eligibilityStatus.data[0];
    }

    return matchedDevice;
  } catch (error) {
    console.error("Error fetching device eligibility:", error);
  }
}

async function loadEvents({ state, commit }, { doorbotId, options }) {
  try {
    let search = encodeURI(JSON.stringify(options));
    const response = await axios.get(
      `v2/devices/${doorbotId}/events?search=${search}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function loadLinks({ state, commit }, { doorbotId }) {
  try {
    const response = await axios.get(`v2/links/${doorbotId}`);
    return response.data;
  } catch (error) {
    throw error.response;
  }
}

async function reportStolen({ state, commit }, { doorbotId }) {
  try {
    const response = await axios.patch(`v2/devices/${doorbotId}/report_stolen`);
    commit(types.UPDATE_DEVICE_PROFILE, response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function loadStatusReports(
  { state, commit },
  { doorbotId, limit, lastTimestamp }
) {
  let params = { limit: limit || 25 };
  if (lastTimestamp) {
    params["last_timestamp"] = lastTimestamp;
  }
  try {
    const response = await axios.get(`v2/devices/${doorbotId}/status_reports`, {
      params,
    });
    commit(types.FETCH_STATUS_REPORT.SUCCESS, response.data);
    return response;
  } catch (error) {
    commit(types.FETCH_STATUS_REPORT.ERROR);
    throw error;
  }
}

async function updateDevice({ state, commit }, { doorbotId, payload }) {
  try {
    payload.metadata = payload.metadata || {};

    if (
      [
        "chime",
        "doorbell",
        "stickup_cam",
        "doorbell_v3",
        "stickup_cam_v3",
      ].includes(state.device.type)
    ) {
      payload.metadata.legacy_fw_migrated = true;
    }

    const response = await axios.patch(`v2/devices/${doorbotId}`, { payload });

    commit(
      `db/account/location/${types.UPDATE_DEVICE_SETTINGS.SUCCESS}`,
      response.data,
      { root: true }
    );
    commit(types.UPDATE_DEVICE_SETTINGS.SUCCESS, response.data);
    return response.data;
  } catch (error) {
    commit(types.UPDATE_DEVICE_SETTINGS.ERROR);
    throw error;
  }
}

async function updateDeviceZones({ state, commit }, { doorbotId, payload }) {
  try {
    const response = await axios.patch(`v2/devices/${doorbotId}/zones`, {
      payload,
    });
    return response.data;
  } catch (error) {
    throw error.response;
  }
}

async function fetchMotionSchedules({ state, commit }, doorbotId) {
  try {
    const response = await axios.get(`v2/devices/${doorbotId}/motion_schedule`);
    commit(types.FETCH_MOTION_SCHEDULE_SETTING.SUCCESS, response.data);
  } catch (error) {
    commit(types.FETCH_MOTION_SCHEDULE_SETTING.ERROR, error);
    throw error.response;
  }
}

async function addMotionSchedules({ state, commit }, { doorbotId, payload }) {
  try {
    await axios.post(`v2/devices/${doorbotId}/motion_schedule`, payload);
  } catch (error) {
    throw error.response;
  }
}

async function deleteMotionSchedules(
  { state, commit },
  { doorbotId, motionScheduleId }
) {
  try {
    await axios.delete(
      `v2/devices/${doorbotId}/motion_schedule?motion_schedule_id=${motionScheduleId}`
    );
  } catch (error) {
    throw error.response;
  }
}

async function createTrigger({ state, commit }, { doorbotId, payload }) {
  try {
    return await axios.post(`v2/devices/${doorbotId}/triggers`, payload);
  } catch (error) {
    throw error.response;
  }
}

async function updateTrigger({}, { doorbotId, triggerId, payload }) {
  try {
    return await axios.patch(
      `v2/devices/${doorbotId}/triggers/${triggerId}`,
      payload
    );
  } catch (error) {
    throw error.response;
  }
}

async function deleteTrigger({ state, commit }, { doorbotId, triggerId }) {
  try {
    return await axios.delete(`v2/devices/${doorbotId}/triggers/${triggerId}`);
  } catch (error) {
    throw error.response;
  }
}

async function createTriggerState(
  { state, commit },
  { doorbotId, triggerId, payload }
) {
  try {
    return await axios.post(
      `v2/devices/${doorbotId}/triggers/${triggerId}/states.json`,
      payload
    );
  } catch (error) {
    throw error.response;
  }
}

async function updateTriggerState(
  { state, commit },
  { doorbotId, triggerId, stateId, payload }
) {
  try {
    return await axios.patch(
      `v2/devices/${doorbotId}/triggers/${triggerId}/states/${stateId}.json`,
      payload
    );
  } catch (error) {
    throw error.response;
  }
}

async function deleteTriggerState(
  { state, commit },
  { doorbotId, triggerId, stateId }
) {
  try {
    return await axios.delete(
      `v2/devices/${doorbotId}/triggers/${triggerId}/states/${stateId}.json`
    );
  } catch (error) {
    throw error.response;
  }
}

async function fetchSetups(
  { state, commit },
  { id, model, page = 1, pageSize = 10 }
) {
  const options = {
    params: { page, page_size: pageSize },
  };
  try {
    console.log(page, pageSize);
    const response = await axios.get(`v2/${model}/${id}/setups`, options);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function lookerRunQueries(
  { state, commit },
  { look_ids = [], device_id }
) {
  // if empty throw look_ids or device_id exception
  const response = await axios.get(`v2/looker/diagnosis`, {
    params: { look_ids, device_id },
  });
  // ignore meta, only useful to review specific looker looks errors in chrome
  return response.data.data;
}

async function lookerRunLook({ state, commit }, { look_id, device_id }) {
  // if empty throw look_ids or device_id exception
  const response = await axios.get(`v2/looker/run_look`, {
    params: { look_id, device_id },
  });
  return response.data;
}

async function loadFirmwares(
  { state, commit },
  { type, component, doorbotId }
) {
  const response = await axios.get(
    `v2/firmwares?type=${type}&component=${component}&doorbot_id=${doorbotId}`
  );
  return response;
}

async function loadDefaultsFirmwares({ state, commit }, { type, doorbotId }) {
  const response = await axios.get(
    `v2/firmwares/defaults?type=${type}&doorbot_id=${doorbotId}`
  );
  return response;
}

async function fetchAvailableFirmwares(
  { commit },
  { type, components, doorbotId }
) {
  // let defaultList = components.reduce((comp, name) => (comp[name] = []), {});
  try {
    let requests = components.map((cname) => {
      return axios
        .get("v2/firmwares", {
          params: {
            component: cname,
            limit: 1000,
            type: type,
            doorbot_id: doorbotId,
          },
        })
        .catch((err) => {
          console.error(err);
          return null;
        });
    });

    let responses = await axios.all(requests);
    let firmwares = responses.map((resp) => get(resp, "data.firmwares", []));

    return zipObject(components, firmwares);
  } catch (e) {
    console.error(e);
    throw e.response;
  }
}

async function fetchFirmwareComponents({ commit }, { type }) {
  try {
    const response = await axios.get(`v2/firmwares/components?type=${type}`);
    return get(response, "data.components", []);
  } catch (e) {
    console.error(e);
    throw e.response;
  }
}

async function loadLastStatusReport({ state, commit }, { doorbotId }) {
  try {
    const response = await axios.get(
      `v2/devices/${doorbotId}/last_status_report`
    );
    commit(types.FETCH_LAST_STATUS_REPORT.SUCCESS, response.data);
    return response;
  } catch (error) {
    commit(types.FETCH_LAST_STATUS_REPORT.ERROR, doorbotId);
    throw error.response;
  }
}

async function getFirmwares({ state, commit }, { doorbotId }) {
  return new Promise((resolve, reject) => {
    axios
      .get(`v2/devices/${doorbotId}/get_firmwares`)
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function setFirmwares({ state, commit }, { doorbotId, payload }) {
  return new Promise((resolve, reject) => {
    axios
      .patch(`v2/devices/${doorbotId}/set_firmwares`, { firmwares: payload })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function execThroughputTest({}, { device_id, device_kind, test_type }) {
  try {
    return await axios.post(`v2/devices/${device_id}/throughput_test`, {
      device_id,
      device_kind,
      test_type,
    });
  } catch (error) {
    throw error;
  }
}

async function executeTestSound({}, { id, device_kind, event_type }) {
  try {
    const response = await axios.post(`v2/devices/${id}/play_sound_test`, {
      id,
      device_kind,
      event_type,
    });

    return response;
  } catch (error) {
    throw error;
  }
}

async function getLastBitrate({ commit }, { doorbotId }) {
  try {
    const response = await axios.get(`v2/devices/${doorbotId}/last_bitrate`);

    return response;
  } catch (error) {
    throw error;
  }
}

export default {
  execThroughputTest,
  executeTestSound,
  fetchDeviceDetailed,
  getDeviceStatus,
  updateDevice,
  updateDeviceZones,
  fetchMotionSchedules,
  fetchAvailableFirmwares,
  fetchFirmwareComponents,
  addMotionSchedules,
  deleteMotionSchedules,
  loadEvents,
  loadLinks,
  loadStatusReports,
  fetchSetups,
  getWarrantyAndEligibilityStatus,
  loadDefaultsFirmwares,
  loadLastStatusReport,
  getFirmwares,
  setFirmwares,
  reportStolen,
  getLastBitrate,
  createTrigger,
  updateTrigger,
  deleteTrigger,
  createTriggerState,
  updateTriggerState,
  deleteTriggerState,
  getDeviceEligibility,
};
